import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { type IntegrationServiceId } from '../../common/integrations'
import { AIAgentType, BaseAIAgentExecutionResult, BaseAIAgentInteractionResult } from '../../models/AIAgent'
import { createStreamingResponseHandler } from '../../common/streamingUtils'
import { StreamingEvent } from '../../models/StreamingAIAgent'

const _BASE_URL_EXECUTE = import.meta.env.REACT_APP_AI_EXECUTE_API_BASE_URL!

export interface ZocksFileAttachment {
  source?: 'zocks'
  fileId: string
}

export interface IntegrationAttachment {
  source: 'integration'
  connector: IntegrationServiceId
  fileId: string
}

export type AIAgentAttachment = ZocksFileAttachment | IntegrationAttachment

export interface AIAgentExecutionPayload {
  type: AIAgentType
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context: { [key: string]: any }
  attachments?: AIAgentAttachment[]
}

export interface ExecuteAIAgentRequest {
  accountId: string
  payload: AIAgentExecutionPayload
}

export interface InteractWithAIAgentRequest {
  accountId: string
  aiAgentId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: { [key: string]: any }
}

export interface InteractWithAIAgentStreamingRequest extends InteractWithAIAgentRequest {
  onStreamingEvent: (event: StreamingEvent) => Promise<void>
}

export interface ExecuteAIAgentStreamingRequest extends ExecuteAIAgentRequest {
  onStreamingEvent: (event: StreamingEvent) => Promise<void>
}

export const aiAgentApi = createApi({
  reducerPath: 'aiAgentApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL_EXECUTE),
  endpoints: (builder) => ({
    executeAiAgent: builder.mutation<BaseAIAgentExecutionResult, ExecuteAIAgentRequest>({
      query: ({ accountId, payload }) => ({
        url: `/accounts/${accountId}/ai-agent/execute`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: { data: BaseAIAgentExecutionResult }) => response.data,
    }),
    interactWithAiAgent: builder.mutation<BaseAIAgentInteractionResult, InteractWithAIAgentRequest>({
      query: ({ accountId, aiAgentId, payload }) => ({
        url: `/accounts/${accountId}/ai-agent/${aiAgentId}/interact`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: { data: BaseAIAgentInteractionResult }) => response.data,
    }),
    interactWithAiAgentStreaming: builder.mutation<undefined, InteractWithAIAgentStreamingRequest>({
      query: ({ accountId, aiAgentId, payload, onStreamingEvent }) => ({
        url: `/accounts/${accountId}/ai-agent/${aiAgentId}/interact/stream`,
        method: 'POST',
        body: payload,
        responseHandler: createStreamingResponseHandler(onStreamingEvent),
      }),
    }),
    executeAiAgentStreaming: builder.mutation<undefined, ExecuteAIAgentStreamingRequest>({
      query: ({ accountId, payload, onStreamingEvent }) => ({
        url: `/accounts/${accountId}/ai-agent/execute/stream`,
        method: 'POST',
        body: payload,
        responseHandler: createStreamingResponseHandler(onStreamingEvent),
      }),
    }),
  }),
})

export const { useExecuteAiAgentMutation, useExecuteAiAgentStreamingMutation, useInteractWithAiAgentMutation, useInteractWithAiAgentStreamingMutation } =
  aiAgentApi
